import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowLeft, House, Trophy, Ticket, Lightning, Confetti, CalendarBlank } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// May Email Capture Contest tracker. Hard-coded May 3-30, 2026 window per
// user spec. Pulls /api/contest/may-2026 and renders the prize hero, the
// running ticket count, the four-week breakdown, and the live current-week
// pacing chip with next-tier hint.
export default function ContestPage({ onBack, onGoHome }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/contest/may-2026`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        console.error("[ContestPage] fetch failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Refresh every 60s so the active week's tier updates as the day's
    // tallies and checkpoints come in.
    const iv = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const fmtPct = (r) => `${(r * 100).toFixed(1)}%`;

  return (
    <div
      className="min-h-screen relative pb-16"
      style={{
        background:
          "radial-gradient(900px 600px at 50% -10%, rgba(250,204,21,0.18), transparent 60%), radial-gradient(800px 600px at 85% 100%, rgba(236,72,153,0.10), transparent 60%), #F8FAFC",
      }}
      data-testid="contest-page"
    >
      <header
        className="sticky top-0 z-30 backdrop-blur-xl"
        style={{
          backgroundColor: "rgba(248,250,252,0.85)",
          borderBottom: "1px solid rgba(148,163,184,0.18)",
        }}
      >
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-1.5 rounded-full hover:bg-slate-100 transition-colors"
            aria-label="Back"
            data-testid="contest-back"
          >
            <ArrowLeft weight="bold" className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-black text-amber-600 leading-tight">
              Email Contest
            </p>
            <h1
              className="text-base font-black leading-tight text-slate-900 truncate"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.01em" }}
            >
              Email Capture Challenge
            </h1>
          </div>
          {onGoHome && (
            <button onClick={onGoHome} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors" data-testid="contest-home">
              <House weight="bold" className="w-5 h-5 text-slate-700" />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {loading || !data ? (
          <div className="rounded-3xl bg-white/80 p-10 text-center text-slate-400" data-testid="contest-loading">
            <Trophy weight="duotone" className="w-12 h-12 mx-auto mb-2 text-slate-300 animate-pulse" />
            Loading the contest…
          </div>
        ) : (
          <>
            {/* Hero — prize callout + headline ticket total */}
            <section
              className="rounded-3xl p-5 sm:p-6 text-white shadow-xl relative overflow-hidden fb-enter"
              style={{
                background: "linear-gradient(160deg,var(--fb-blue-dark) 0%,var(--fb-blue) 50%,#1E40AF 100%)",
                "--fb-delay": "0ms",
              }}
              data-testid="contest-hero"
            >
              <span
                className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none opacity-25 blur-2xl"
                style={{ backgroundColor: "var(--fb-accent)" }}
                aria-hidden
              />
              <span
                className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none opacity-20 blur-2xl"
                style={{ backgroundColor: "#EC4899" }}
                aria-hidden
              />
              <div className="relative z-10 flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-black text-white/80">
                    Drawing Prize
                  </p>
                  <h2
                    className="text-3xl sm:text-4xl font-black tracking-tight mt-1"
                    style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.02em" }}
                  >
                    ${data.prize_per_crew} per crew
                  </h2>
                  <p className="text-xs text-white/85 mt-1 max-w-[18rem]">
                    {data.total_winning_stores} stores will be drawn at random — every entry helps.
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/75">Tickets</p>
                  <p
                    className="text-5xl font-black leading-none mt-1 tabular-nums"
                    style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                    data-testid="contest-total-tickets"
                  >
                    {data.total_tickets}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-white/70 mt-1">
                    of {data.max_possible}
                  </p>
                </div>
              </div>

              {/* Progress bar to max possible */}
              <div className="relative z-10 mt-5">
                <div className="h-2 rounded-full bg-white/15 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, (data.total_tickets / Math.max(1, data.max_possible)) * 100)}%`,
                      background: "linear-gradient(90deg,var(--fb-accent),#F59E0B)",
                      transition: "width 600ms ease",
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px] font-semibold text-white/80">
                  <span data-testid="contest-locked">{data.locked_tickets} locked in</span>
                  <span data-testid="contest-projected">
                    {data.total_tickets - data.locked_tickets > 0
                      ? `+${data.total_tickets - data.locked_tickets} pacing this week`
                      : data.is_active
                        ? "Hit 30% to start earning"
                        : ""}
                  </span>
                </div>
              </div>

              {/* Date strip + countdown */}
              <div className="relative z-10 mt-5 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/15 backdrop-blur rounded-full px-3 py-1">
                  <CalendarBlank weight="fill" className="w-3.5 h-3.5" />
                  {fmtDate(data.starts)} – {fmtDate(data.ends)}
                </span>
                {data.is_active ? (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-amber-400 text-amber-950 rounded-full px-3 py-1 shadow"
                    data-testid="contest-countdown"
                  >
                    <Lightning weight="fill" className="w-3.5 h-3.5" />
                    {data.days_left} day{data.days_left === 1 ? "" : "s"} left
                  </span>
                ) : data.days_left > 0 ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/15 rounded-full px-3 py-1">
                    Starts in {data.days_left} day{data.days_left === 1 ? "" : "s"}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/15 rounded-full px-3 py-1">
                    Contest closed
                  </span>
                )}
              </div>
            </section>

            {/* Tier table — quick reference card */}
            <section
              className="rounded-2xl bg-white shadow-sm p-4 fb-enter"
              style={{ border: "1px solid rgba(148,163,184,0.18)", "--fb-delay": "80ms" }}
              data-testid="contest-tier-table"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-black text-slate-500 mb-3">
                Ticket Tiers
              </p>
              <div className="space-y-2">
                {data.tier_table.map((t) => (
                  <div
                    key={t.label}
                    className="flex items-center justify-between gap-3 rounded-xl px-3 py-2"
                    style={{
                      backgroundColor:
                        t.tickets === 3 ? "rgba(16,185,129,0.10)" :
                        t.tickets === 2 ? "rgba(0,102,255,0.10)" :
                        t.tickets === 1 ? "rgba(250,204,21,0.12)" :
                        "rgba(148,163,184,0.10)",
                    }}
                  >
                    <p className="text-sm font-bold text-slate-900">{t.label}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-black text-slate-900">
                      <Ticket weight="fill" className="w-4 h-4 text-amber-500" />
                      {t.tickets}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Weekly breakdown */}
            <section
              className="rounded-2xl bg-white shadow-sm p-4 fb-enter"
              style={{ border: "1px solid rgba(148,163,184,0.18)", "--fb-delay": "160ms" }}
              data-testid="contest-weeks"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-black text-slate-500 mb-3">
                Week-by-Week
              </p>
              <div className="space-y-3">
                {data.weeks.map((w) => {
                  const pct = w.transactions > 0 ? Math.min(100, w.rate * 100) : 0;
                  const isActive = w.status === "active";
                  const isCompleted = w.status === "completed";
                  const tierBarBg =
                    w.tickets === 3 ? "linear-gradient(90deg,#10B981,#34D399)" :
                    w.tickets === 2 ? "linear-gradient(90deg,var(--fb-blue),#3B82F6)" :
                    w.tickets === 1 ? "linear-gradient(90deg,#F59E0B,var(--fb-accent))" :
                    "linear-gradient(90deg,#94A3B8,#CBD5E1)";
                  return (
                    <div
                      key={w.num}
                      className={`rounded-xl px-3 py-3 transition-all ${
                        isActive
                          ? "ring-2 ring-amber-400 bg-amber-50"
                          : isCompleted
                            ? "bg-slate-50"
                            : "bg-slate-50/50"
                      }`}
                      data-testid={`contest-week-${w.num}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p
                              className="text-sm font-black text-slate-900"
                              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                            >
                              {w.label}
                            </p>
                            {isActive && (
                              <span className="text-[9px] uppercase tracking-wider font-black bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
                                Live
                              </span>
                            )}
                            {isCompleted && (
                              <span className="text-[9px] uppercase tracking-wider font-black bg-emerald-600 text-white px-1.5 py-0.5 rounded-full">
                                Locked
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {fmtDate(w.start)} – {fmtDate(w.end)}
                            {w.transactions > 0 && (
                              <> · <span className="font-semibold text-slate-700">{w.emails}</span> emails / <span className="font-semibold text-slate-700">{w.transactions}</span> tx</>
                            )}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p
                            className="text-2xl font-black leading-none tabular-nums text-slate-900"
                            style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                          >
                            {w.transactions > 0 ? fmtPct(w.rate) : "—"}
                          </p>
                          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-0.5">
                            Capture
                          </p>
                        </div>
                        <div
                          className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-2xl text-white shadow"
                          style={{ background: tierBarBg }}
                        >
                          <span
                            className="text-2xl font-black leading-none"
                            style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                          >
                            {w.tickets}
                          </span>
                        </div>
                      </div>

                      {/* Progress bar to next tier */}
                      <div className="mt-2.5">
                        <div className="h-1.5 rounded-full bg-white overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${pct}%`,
                              background: tierBarBg,
                              transition: "width 600ms ease",
                            }}
                          />
                        </div>
                      </div>

                      {isActive && w.next_tier && (
                        <p className="mt-2 text-[11px] font-semibold text-amber-800">
                          {w.next_tier.emails_needed && w.next_tier.emails_needed > 0
                            ? <>+{w.next_tier.emails_needed} more emails to hit <span className="font-black">{w.next_tier.label}</span> ({w.next_tier.tickets} tickets)</>
                            : <>Hit <span className="font-black">{w.next_tier.label}</span> for {w.next_tier.tickets} tickets</>}
                        </p>
                      )}
                      {isActive && !w.next_tier && w.tickets === 3 && (
                        <p className="mt-2 text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1">
                          <Confetti weight="fill" className="w-3 h-3" /> Maxed at 3 tickets — keep it going!
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Eligibility footer */}
            <p className="text-[11px] text-slate-500 text-center px-4 leading-relaxed">
              Anyone on the active roster on May 30 is eligible. {data.winning_stores_per_region} winning stores per region · {data.total_winning_stores} total stores drawn by WowTown.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
